import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import SchoolPicker from '../components/SchoolPicker';
import apiService from '../services/api';
import { resolveChallengeIcon } from '../constants/challengeTypes';
// import { PARTY_SIZES, MATCH_MODES, defaultPartyForType, TYPE_BY_VALUE } from '../constants/challengeTypes';
import '../styles/StaffPages.css';

const toLocalInput = iso => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = value => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toIso = localValue => {
  if (!localValue) return null;
  const date = new Date(localValue);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

const adminTimeZoneLabel = () => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  try {
    const short = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
      .formatToParts(new Date())
      .find(part => part.type === 'timeZoneName')?.value;
    return short ? `${tz} (${short})` : tz;
  } catch (error) {
    return tz;
  }
};

const formatGoesLive = iso => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const emptyForm = (schoolId, presets) => ({
  school: schoolId || '',
  preset: presets[0]?.id || '',
  start_at: '',
  end_at: '',
  reward_points: '',
  // all_schools: false,
  // party_size: 'solo',
  // match_mode: 'together',
});

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3.5 4.5h9M6 4.5V3.2A.7.7 0 0 1 6.7 2.5h2.6a.7.7 0 0 1 .7.7V4.5M12.2 4.5l-.4 8.1a1 1 0 0 1-1 .9H5.2a1 1 0 0 1-1-.9L3.8 4.5" stroke="#b91c1c" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6.5 7v4M9.5 7v4" stroke="#b91c1c" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const PencilIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M11.2 2.3l2.5 2.5-8.4 8.4H2.8v-2.5l8.4-8.4z" stroke="#111827" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M9.8 3.7l2.5 2.5" stroke="#111827" strokeWidth="1.3" />
  </svg>
);

const SuperuserSchoolChallenges = () => {
  const navigate = useNavigate();
  const timeZoneLabel = adminTimeZoneLabel();
  const [schools, setSchools] = useState([]);
  const [enabledPresets, setEnabledPresets] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm('', []));
  const schoolId = selectedSchool?.id || '';

  useEffect(() => {
    const boot = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();
        if (!currentUser?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }
        const schoolsData = await apiService.getSchools();
        const schoolList = [...(Array.isArray(schoolsData) ? schoolsData : schoolsData.results || [])].sort((a, b) => {
          const aActive = a?.is_active === false ? 1 : 0;
          const bActive = b?.is_active === false ? 1 : 0;
          if (aActive !== bActive) return aActive - bActive;
          return (a?.name || '').localeCompare(b?.name || '');
        });
        setSchools(schoolList);
      } catch (error) {
        if (error.response?.status === 401) {
          navigate('/login/');
          return;
        }
        setPageError(error.response?.data?.error || 'Failed to load challenge schedule.');
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, [navigate]);

  useEffect(() => {
    if (!schoolId) {
      setItems([]);
      setEnabledPresets([]);
      setForm(emptyForm('', []));
      setEditingId(null);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setPageError('');
        const [challengesData, catalogData] = await Promise.all([
          apiService.getSchoolChallenges(schoolId),
          apiService.getSchoolChallengeCatalog(schoolId),
        ]);
        if (cancelled) return;
        const presets = (catalogData.results || []).filter(row => row.selected);
        setItems(challengesData.results || []);
        setEnabledPresets(presets);
        setEditingId(null);
        setFormError('');
        setForm(emptyForm(schoolId, presets));
      } catch (error) {
        if (!cancelled) {
          setPageError(error.response?.data?.error || 'Failed to load challenge schedule.');
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  const onChange = key => event => {
    setFormError('');
    setForm(prev => ({ ...prev, [key]: event.target.value }));
  };

  const resetForm = () => {
    setEditingId(null);
    setFormError('');
    setForm(emptyForm(schoolId, enabledPresets));
  };

  const openEdit = item => {
    setEditingId(item.id);
    setFormError('');
    setForm({
      school: item.school,
      preset: item.preset || '',
      start_at: toLocalInput(item.start_at),
      end_at: toLocalInput(item.end_at),
      reward_points: item.reward_points || '',
    });
  };

  const onSubmit = async event => {
    event.preventDefault();
    const startAt = toIso(form.start_at);
    const endAt = toIso(form.end_at);
    const rewardPoints = parseInt(form.reward_points, 10);
    if (!editingId && !schoolId) {
      setFormError('Please select a school');
      return;
    }
    if (!editingId && !form.preset) {
      setFormError('Pick a challenge.');
      return;
    }
    if (!startAt || !endAt) {
      setFormError('Set both start and end.');
      return;
    }
    if (new Date(endAt) <= new Date(startAt)) {
      setFormError('End must be after start.');
      return;
    }
    if (!rewardPoints || rewardPoints < 1) {
      setFormError('Set how many points are awarded.');
      return;
    }
    const payload = {
      start_at: startAt,
      end_at: endAt,
      reward_points: rewardPoints,
    };
    if (!editingId) {
      payload.school = schoolId;
      payload.preset = form.preset;
    }
    try {
      setSaving(true);
      setFormError('');
      if (editingId) {
        await apiService.updateSchoolChallenge(editingId, payload);
      } else {
        await apiService.createSchoolChallenge(payload);
      }
      const data = await apiService.getSchoolChallenges(schoolId);
      setItems(data.results || []);
      resetForm();
    } catch (error) {
      const data = error.response?.data;
      setFormError(data?.error || Object.values(data || {}).flat().join('\n') || 'Failed to save challenge.');
    } finally {
      setSaving(false);
    }
  };

  const editingItem = editingId ? items.find(item => String(item.id) === String(editingId)) : null;

  const onDelete = async item => {
    if (!window.confirm(`Remove "${item.title}" from this school?`)) return;
    try {
      setPageError('');
      await apiService.deleteSchoolChallenge(item.id);
      setItems(prev => prev.filter(entry => String(entry.id) !== String(item.id)));
      if (String(editingId) === String(item.id)) {
        resetForm();
      }
    } catch (error) {
      setPageError(error.response?.data?.error || 'Failed to delete challenge.');
    }
  };

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton text="Back to Superuser" to="/superuser/dashboard/" />
          <div className="staff-page-header-row">
            <div>
              <h1 className="staff-page-header">Challenge schedule</h1>
              <p className="staff-page-subheader">
                Set when enabled challenges go live for this school.
              </p>
            </div>
            {schools.length > 0 ? (
              <div className="hc-challenge-school-picker">
                <SchoolPicker schools={schools} value={selectedSchool} onChange={setSelectedSchool} />
              </div>
            ) : null}
          </div>

          {pageError ? <div style={{ color: '#b00020', marginBottom: 16, whiteSpace: 'pre-wrap' }}>{pageError}</div> : null}
          {loading ? (
            <div>Loading challenge schedule...</div>
          ) : !selectedSchool ? (
            <div style={{ opacity: 0.7 }}>Please select a school</div>
          ) : (
            <div className="hc-challenge-schedule">
              <div>
                <div className="hc-challenge-schedule-head">
                  <span />
                  <span>Challenge</span>
                  <span>Goes live</span>
                  <span>Points</span>
                  <span />
                </div>
                {items.length === 0 ? (
                  <div style={{ opacity: 0.7, padding: '16px 8px' }}>
                    No challenges scheduled for {selectedSchool.name} yet.
                  </div>
                ) : (
                  items.map(item => {
                    const icon = resolveChallengeIcon(item);
                    return (
                      <div key={item.id} className="hc-challenge-schedule-row">
                        <span className="hc-challenge-schedule-icon">
                          {icon ? <img src={icon} alt="" width={18} height={18} /> : null}
                        </span>
                        <span className="hc-challenge-schedule-title">{item.title}</span>
                        <span className="hc-challenge-schedule-live">{formatGoesLive(item.start_at)}</span>
                        <span className="hc-challenge-schedule-points">{item.reward_points}</span>
                        <span className="hc-challenge-schedule-actions">
                          <button type="button" className="hc-challenge-schedule-edit" onClick={() => openEdit(item)} aria-label={`Edit ${item.title}`}>
                            <PencilIcon />
                          </button>
                          <button type="button" className="hc-challenge-schedule-edit" onClick={() => onDelete(item)} aria-label={`Delete ${item.title}`}>
                            <TrashIcon />
                          </button>
                        </span>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="hc-challenge-schedule-form">
                <h2 className="hc-challenge-schedule-form-title">{editingId ? 'Edit challenge' : 'Schedule challenge'}</h2>
                {formError ? <div style={{ color: '#b00020', marginBottom: 16, whiteSpace: 'pre-wrap' }}>{formError}</div> : null}
                <form onSubmit={onSubmit} className="reward-form reward-form--compact">
                  <div className="form-group">
                    <label className="form-label" htmlFor="schedule-preset">Challenge</label>
                    <select
                      id="schedule-preset"
                      className="form-select"
                      value={form.preset}
                      onChange={onChange('preset')}
                      disabled={Boolean(editingId)}
                      required={!editingId}
                    >
                      {editingId && editingItem && !enabledPresets.some(preset => String(preset.id) === String(editingItem.preset)) ? (
                        <option value={editingItem.preset || ''}>{editingItem.title}</option>
                      ) : null}
                      {enabledPresets.map(preset => (
                        <option key={preset.id} value={preset.id}>{preset.title}</option>
                      ))}
                    </select>
                    {enabledPresets.length === 0 && !editingId ? (
                      <div className="hc-challenge-tz">Enable challenges for this school first.</div>
                    ) : null}
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="schedule-start">Starts</label>
                    <input id="schedule-start" type="datetime-local" value={form.start_at} onChange={onChange('start_at')} className="form-input" required />
                    <div className="hc-challenge-tz">{timeZoneLabel}</div>
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="schedule-end">Ends</label>
                    <input id="schedule-end" type="datetime-local" value={form.end_at} onChange={onChange('end_at')} className="form-input" required />
                    <div className="hc-challenge-tz">{timeZoneLabel}</div>
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="schedule-points">Points awarded</label>
                    <input id="schedule-points" type="number" min="1" value={form.reward_points} onChange={onChange('reward_points')} className="form-input" required />
                  </div>
                  {/*
                  <div className="form-group">
                    <label className="form-label">Party size</label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {PARTY_SIZES.map(size => (
                        <button key={size.value} type="button">{size.label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Play style</label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {MATCH_MODES.map(mode => (
                        <button key={mode.value} type="button">{mode.label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      <input type="checkbox" />
                      Assign to all active schools
                    </label>
                  </div>
                  */}
                  <div className="form-actions">
                    {editingId ? (
                      <button
                        type="button"
                        className="staff-reward-action-btn staff-reward-action-btn--danger"
                        style={{ marginRight: 'auto' }}
                        onClick={() => editingItem && onDelete(editingItem)}
                        disabled={saving}
                      >
                        Delete
                      </button>
                    ) : null}
                    <button type="button" className="cancel-button" onClick={resetForm} disabled={saving}>
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="staff-page-button submit-button"
                      disabled={saving || (!editingId && enabledPresets.length === 0)}
                    >
                      <span className="staff-page-button-text">
                        {saving ? 'Saving...' : editingId ? 'Save changes' : 'Schedule challenge'}
                      </span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperuserSchoolChallenges;
